<template>
  <div class="w-full mx-auto select-none">
    <!-- Yearly view navigation -->
    <div v-if="yearlyView" class="flex items-center justify-between mb-4">
      <button @click="changeYear(-1)" class="flex items-center justify-center p-2 rounded-lg w-[40px] h-[40px] text-neutral-800 hover:bg-neutral-200 bg-neutral-100">
        <ChevronLeft :size="20" :stroke-width="2.25" />
      </button>
      <h2 class="text-xl font-bold">{{ currentDate.getFullYear() }}</h2>
      <button @click="changeYear(1)" class="flex items-center justify-center p-2 rounded-lg w-[40px] h-[40px] text-neutral-800 hover:bg-neutral-200 bg-neutral-100">
        <ChevronRight :size="20" :stroke-width="2.25" />
      </button>
    </div>
    <!-- Calendar grid -->
    <div :class="{
      'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-2': yearlyView,
      '1.5xl:flex 1.5xl:space-x-1.5': !yearlyView
    }">
      <div
        v-for="(month, index) in yearlyView ? allMonthsInYear : [currentDate, nextMonth]"
        :key="index"
        :class="{
          'hidden 1.5xl:block': index === 1 && !yearlyView,
          'border-solid border-neutral-300 border flex-1 p-2 rounded-lg': true,
          'mb-2 1.5xl:mb-0': !yearlyView,
          'text-xs': yearlyView
        }"
      >
        <!-- Month header -->
        <div class="flex justify-between items-center mb-0.5">
          <!-- Previous month button -->
          <button
            v-if="!yearlyView && (index === 0 || !isLargeScreen)"
            @click="changeMonth(-1)"
            class="flex items-center justify-center p-2 w-[40px] h-[40px] text-sm rounded-lg text-neutral-800 hover:bg-neutral-200 bg-neutral-100"
          >
            <ChevronLeft :size="20" :stroke-width="2.25" />
          </button>
          <!-- Empty cell for alignment -->
          <div v-else-if="!yearlyView" class="w-9"></div>
          <!-- Month name -->
          <h2 :class="{'text-sm font-bold flex-grow text-center': !yearlyView, 'text-xs font-semibold w-full text-center': yearlyView}">
            {{ formatMonthYear(month) }}
          </h2>
          <!-- Next month button -->
          <button
            v-if="!yearlyView && (index === 1 || !isLargeScreen)"
            @click="changeMonth(1)"
            class="flex items-center justify-center p-2 w-[40px] h-[40px] text-sm rounded-lg text-neutral-800 hover:bg-neutral-200 bg-neutral-100"
          >
            <ChevronRight :size="20" :stroke-width="2.25" />
          </button>
          <!-- Empty cell for alignment -->
          <div v-else-if="!yearlyView" class="w-9"></div>
        </div>
        <!-- Days of the week header -->
        <div class="grid w-full grid-cols-7 gap-1" :class="{ '1.5xl:w-72': !yearlyView }">
          <div
            v-for="day in daysOfWeek"
            :key="day"
            :class="{'text-center font-medium text-neutral-900 text-sm py-1': !yearlyView, 'text-center font-medium text-neutral-900 text-xs': yearlyView}"
          >
            {{ yearlyView ? day.charAt(0) : day }}
          </div>
          <!-- Calendar dates -->
          <div
            v-for="(date, dateIndex) in getCalendarDates(month)"
            :key="dateIndex"
            :class="[ 
              'aspect-square flex items-center justify-center rounded-md',
              yearlyView ? 'text-xs' : 'text-sm',
              date ? [
                // Cursor styles
                {
                  'cursor-not-allowed': !isDateSelectable(date) || (getDateEventType(date) && !yearlyView) || (!yearlyView && eventType === null),
                  'cursor-pointer': isDateSelectable(date) && !getDateEventType(date) && !yearlyView && eventType !== null,
                },
                // Event-specific or default styles
                getDateEventType(date)
                  ? [
                      getEventTypeColors(getDateEventType(date)).text,
                      getEventTypeColors(getDateEventType(date)).border,
                      getEventTypeColors(getDateEventType(date)).bg,
                    ]
                  : isDateSelectable(date) && !yearlyView && eventType !== null
                  ? ['bg-neutral-100', 'hover:bg-neutral-200', 'text-neutral-900']
                  : ['text-neutral-300'],
                // Selection and range styles
                {
                  'bg-neutral-800 text-white hover:bg-neutral-700':
                    isSelectedStart(date) ||
                    isSelectedEnd(date) ||
                    (isInRange(date) && !getDateEventType(date)),
                },
              ] : ['bg-transparent']
            ]"
            @click="date && eventType !== null && !yearlyView && selectDate(date)"
            @mouseover="date && getDateEventType(date) && showEventInfo(date, $event)"
            @mouseleave="hideEventInfo"
          >
            {{ date ? date.getDate() : '' }}
          </div>
        </div>
      </div>
    </div>

    <!-- Event Info Popover -->
  <EventInfoPopover
    v-if="eventInfo"
    :event="activeEvent"
    :initial-x="eventInfoX"
    :initial-y="eventInfoY"
    @close="hideEventInfo"
    :is-visible="eventInfo"
  />
  </div>
</template>

<script setup>
import { ref, computed, defineEmits, defineProps, watch, onMounted, onUnmounted } from "vue";
import { useEventsStore } from "@/stores/eventsStore";
import { ChevronLeft, ChevronRight } from "lucide-vue-next";
import EventInfoPopover from "@/components/ui/AdminDashboard/Popovers/EventInfoPopover.vue";

// Define props
const props = defineProps({
  selectedRange: {
    type: Object,
    default: () => ({ start: null, end: null }),
  },
  eventType: {
    type: String,
    default: null,
  },
  user: {
    type: Object,
    required: true,
  },
  yearlyView: {
    type: Boolean,
    default: false,
  },
});

// Define emits
const emit = defineEmits(["range-selected"]);

const eventsStore = useEventsStore();

// Refs
const currentDate = ref(new Date());
const selectedStartDate = ref(null);
const selectedEndDate = ref(null);
const isLargeScreen = ref(false);
const eventInfo = ref(false);
const eventInfoX = ref(0);
const eventInfoY = ref(0);
const activeEvent = ref(null);

// Constants
const daysOfWeek = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];

// Computed properties
const nextMonth = computed(() => {
  const next = new Date(currentDate.value);
  next.setMonth(next.getMonth() + 1);
  return next;
});

const allMonthsInYear = computed(() => {
  const year = currentDate.value.getFullYear();
  return Array.from({ length: 12 }, (_, i) => new Date(year, i, 1));
});

/**
 * Show the event info popover.
 * @param {Date} date - The date of the event.
 * @param {MouseEvent} event - The mouse event.
 */
 const showEventInfo = (date, event) => {
  const eventType = getDateEventType(date);
  if (eventType) {
    const userEvent = props.user.events.find(
      (e) => new Date(e.date).toDateString() === date.toDateString()
    );
    activeEvent.value = {
      type: eventType,
      date: date,
      replacement: userEvent.replacement
    };
    eventInfoX.value = event.clientX;
    eventInfoY.value = event.clientY;
    eventInfo.value = true;
  }
};

/**
 * Hide the event info popover.
 */
const hideEventInfo = () => {
  eventInfo.value = false;
};

// Lifecycle hooks
onMounted(() => {
  checkScreenSize();
  window.addEventListener("resize", checkScreenSize);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkScreenSize);
});

// Watchers
watch(
  () => props.selectedRange,
  (newRange) => {
    selectedStartDate.value = newRange.start ? new Date(newRange.start) : null;
    selectedEndDate.value = newRange.end ? new Date(newRange.end) : null;
  },
  { deep: true, immediate: true }
);

// Helper functions
/**
 * Check the screen size and update the isLargeScreen ref.
 */
const checkScreenSize = () => {
  isLargeScreen.value = window.innerWidth >= 1350;
};

/**
 * Format a date to display the month and year.
 * @param {Date} date - The date to format.
 * @returns {String} - The formatted month and year.
 */
const formatMonthYear = (date) => {
  const monthYear = date.toLocaleString("da-DK", {
    month: "long",
    year: "numeric",
  });
  return monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
};

/**
 * Change the current year by a given delta.
 * @param {Number} delta - The number of years to change.
 */
const changeYear = (delta) => {
  currentDate.value = new Date(
    currentDate.value.getFullYear() + delta,
    currentDate.value.getMonth(),
    1
  );
};

/**
 * Change the current month by a given delta.
 * @param {Number} delta - The number of months to change.
 */
const changeMonth = (delta) => {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() + delta,
    1
  );
};

/**
 * Generate the calendar dates for a given month.
 * @param {Date} monthDate - The date representing the month.
 * @returns {Array} - An array of dates for the calendar.
 */
const getCalendarDates = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const dates = [];
  let daysBeforeFirstDay = firstDay.getDay() - 1;
  if (daysBeforeFirstDay === -1) daysBeforeFirstDay = 6; // Adjust for Sunday

  // Add empty spots for days before the first of the month
  for (let i = 0; i < daysBeforeFirstDay; i++) {
    dates.push(null);
  }

  // Add the actual dates of the month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    dates.push(new Date(year, month, i));
  }

  // Add empty spots to complete the last week
  const remainingDays = 7 - (dates.length % 7);
  if (remainingDays < 7) {
    for (let i = 0; i < remainingDays; i++) {
      dates.push(null);
    }
  }

  return dates;
};

/**
 * Check if two dates are in the same week.
 * Adjusts for weeks starting on Monday.
 * @param {Date} date1 - The first date.
 * @param {Date} date2 - The second date.
 * @returns {Boolean} - True if the dates are in the same week, false otherwise.
 */
const isSameWeek = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);

  // Adjust dates to handle weeks starting on Monday
  const day1 = d1.getDay() || 7;
  const day2 = d2.getDay() || 7;

  d1.setDate(d1.getDate() + 4 - day1);
  d2.setDate(d2.getDate() + 4 - day2);

  return (
    Math.floor(d1.getTime() / 8.64e7) === Math.floor(d2.getTime() / 8.64e7)
  );
};

/**
 * Determine if a date is selectable based on various criteria.
 * @param {Date} date - The date to check.
 * @returns {Boolean} - True if the date is selectable, false otherwise.
 */
 const isDateSelectable = (date) => {
  /**const today = new Date();
  const threeWeeksFromNow = new Date(
    today.getTime() + 21 * 24 * 60 * 60 * 1000
  ); **/
  const deadline = props.eventType
    ? new Date(eventsStore.getDeadline(props.eventType))
    : null;

  // Check if the date has an existing event
  if (getDateEventType(date)) return false;
  if (date.getDay() === 1) return false; // Monday
  // if (date < threeWeeksFromNow) return false;

  // Check if the date is within a year after the deadline
  if (deadline) {
    const oneYearAfterDeadline = new Date(deadline);
    oneYearAfterDeadline.setFullYear(oneYearAfterDeadline.getFullYear() + 1);
    if (date > oneYearAfterDeadline) return false;
  }

  if (
    props.eventType === "Friweekender" &&
    date.getDay() !== 0 &&
    date.getDay() !== 6
  )
    return false;

  if (
    [
      "Samtidighedsferieuger",
      "Særlige feriedage",
      "Forlagt tjeneste",
      "Kursusvirksomhed",
      "Sygdom",
    ].includes(props.eventType)
  ) {
    if (selectedStartDate.value && !selectedEndDate.value) {
      const dayDiff =
        Math.abs(date.getTime() - selectedStartDate.value.getTime()) /
        (24 * 60 * 60 * 1000);
      if (dayDiff > 6 || !isSameWeek(date, selectedStartDate.value))
        return false;
    }
  }

  return true;
};

/**
 * Handle date selection based on event type.
 * @param {Date} date - The date to select.
 */
const selectDate = (date) => {
  if (!isDateSelectable(date)) return;

  if (
    props.eventType === "Søgnedage" ||
    props.eventType === "Erstatningsfridag"
  ) {
    selectedStartDate.value = date;
    selectedEndDate.value = date;
  } else if (props.eventType === "Friweekender") {
    const saturday =
      date.getDay() === 6
        ? date
        : new Date(date.getTime() - 24 * 60 * 60 * 1000);
    const sunday =
      date.getDay() === 0
        ? date
        : new Date(date.getTime() + 24 * 60 * 60 * 1000);
    selectedStartDate.value = saturday;
    selectedEndDate.value = sunday;
  } else if (
    [
      "Samtidighedsferieuger",
      "Særlige feriedage",
      "Forlagt tjeneste",
      "Kursusvirksomhed",
      "Sygdom",
    ].includes(props.eventType)
  ) {
    if (
      !selectedStartDate.value ||
      (selectedStartDate.value && selectedEndDate.value)
    ) {
      selectedStartDate.value = date;
      selectedEndDate.value = null;
    } else if (isSameWeek(date, selectedStartDate.value)) {
      selectedEndDate.value =
        date > selectedStartDate.value ? date : selectedStartDate.value;
      selectedStartDate.value =
        date < selectedStartDate.value ? date : selectedStartDate.value;
    }
  } else {
    // Default behavior for other event types
    if (
      !selectedStartDate.value ||
      (selectedStartDate.value && selectedEndDate.value)
    ) {
      selectedStartDate.value = date;
      selectedEndDate.value = null;
    } else {
      if (date < selectedStartDate.value) {
        selectedEndDate.value = selectedStartDate.value;
        selectedStartDate.value = date;
      } else {
        selectedEndDate.value = date;
      }
    }
  }

  emitRangeSelected();
};

/**
 * Get the color classes for a given event type.
 * @param {String} eventType - The event type.
 * @returns {Object} - The color classes for the event type.
 */
const getEventTypeColors = (eventType) => {
  switch (eventType) {
    case "Samtidighedsferieuger":
      return {
        border: "border-green-400 border-solid border",
        text: "text-green-400",
        bg: "bg-green-50",
      };
    case "Særlige feriedage":
      return {
        border: "border-purple-400 border-solid border",
        text: "text-purple-400",
        bg: "bg-purple-50",
      };
    case "Søgnedage":
      return {
        border: "border-amber-400 border-solid border",
        text: "text-amber-400",
        bg: "bg-amber-50",
      };
    case "Friweekender":
      return {
        border: "border-pink-400 border-solid border",
        text: "text-pink-400",
        bg: "bg-pink-50",
      };
    case "Forlagt tjeneste":
      return {
        border: "border-teal-400 border-solid border",
        text: "text-teal-400",
        bg: "bg-teal-50",
      };
    case "Kursusvirksomhed":
      return {
        border: "border-lime-500 border-solid border",
        text: "text-lime-500",
        bg: "bg-lime-50",
      };
    case "Erstatningsfridag":
      return {
        border: "border-gray-400 border-solid border",
        text: "text-gray-400",
        bg: "bg-gray-100",
      };
    case "Sygdom":
      return {
        border: "border-red-400 border-solid border",
        text: "text-red-400",
        bg: "bg-red-50",
      };
    default:
      return {
        border: "border-neutral-400 border-solid border",
        text: "text-neutral-400",
        bg: "bg-neutral-100",
      };
  }
};

/**
 * Get the event type for a given date.
 * @param {Date} date - The date to check.
 * @returns {String|null} - The event type or null if no event.
 */
const getDateEventType = (date) => {
  const event = props.user.events.find(
    (event) => new Date(event.date).toDateString() === date.toDateString()
  );
  return event ? event.type : null;
};

/**
 * Check if a date is within the selected range.
 * @param {Date} date - The date to check.
 * @returns {Boolean} - True if the date is within the range, false otherwise.
 */
const isInRange = (date) => {
  if (!selectedStartDate.value || !selectedEndDate.value) return false;

  if (
    [
      "Samtidighedsferieuger",
      "Særlige feriedage",
      "Forlagt tjeneste",
      "Kursusvirksomhed",
      "Sygdom",
    ].includes(props.eventType)
  ) {
    return date >= selectedStartDate.value && date <= selectedEndDate.value;
  }

  return (
    date > selectedStartDate.value &&
    date < selectedEndDate.value &&
    !hasMonday(selectedStartDate.value, selectedEndDate.value)
  );
};

/**
 * Check if a date is the start of the selected range.
 * @param {Date} date - The date to check.
 * @returns {Boolean} - True if the date is the start, false otherwise.
 */
const isSelectedStart = (date) => {
  return (
    selectedStartDate.value &&
    date.toDateString() === selectedStartDate.value.toDateString()
  );
};

/**
 * Check if a date is the end of the selected range.
 * @param {Date} date - The date to check.
 * @returns {Boolean} - True if the date is the end, false otherwise.
 */
 const isSelectedEnd = (date) => {
  return (
    selectedEndDate.value &&
    date.toDateString() === selectedEndDate.value.toDateString()
  );
};

/**
 * Check if there is a Monday between two dates.
 * @param {Date} startDate - The start date.
 * @param {Date} endDate - The end date.
 * @returns {Boolean} - True if there is a Monday, false otherwise.
 */
const hasMonday = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  while (start <= end) {
    if (start.getDay() === 1) return true;
    start.setDate(start.getDate() + 1);
  }

  return false;
};

/**
 * Emit the selected date range.
 */
const emitRangeSelected = () => {
  emit("range-selected", {
    start: selectedStartDate.value,
    end: selectedEndDate.value,
  });
};
</script>